/**
 * inbox-pages.ts — pure helpers for the admin "All" tab cursor pagination (Phase 12 follow-up).
 *
 * The admin all-accounts monitoring view (ChatWorkspace, allAccountsMode) pages the
 * fleet-wide inbox via useInfiniteQuery against GET /inbox — the server admin branch, keyset
 * by conversation.last_message_at DESC (WHERE last_message_at < cursor). Previously it fetched
 * a single 100-row page and discarded hasMore/nextCursor, silently capping the monitoring
 * view at the 100 most recently active conversations (a real problem for an agency with tens
 * of accounts + a creator carrying 25k+ conversations). These two functions carry the only
 * non-trivial logic:
 *
 *   - flattenInboxPages: collapse the paged InfiniteData into one ordered array, de-duping by
 *     conversation id. Keyset page boundaries AND live inbox:delta refetches can surface the
 *     same conversation on two pages (a row whose lastMessageAt updates jumps to page 1), so a
 *     naive concat would render duplicate rows and duplicate React keys. First occurrence wins,
 *     preserving the server's newest-first order.
 *   - nextInboxCursor: derive the next keyset cursor for getNextPageParam. The server reports
 *     `hasMore` but does not (yet) emit `nextCursor`, so fall back to the last row's
 *     lastMessageAt (the keyset key), preferring page.nextCursor should a future backend send
 *     it. undefined ⇒ pagination is exhausted (getNextPageParam stops, no more pages).
 *
 * Pure + React-free (only type-only imports) so they unit-test in a node env
 * (tests/unit/inbox-pages.test.ts) and can be reasoned about apart from the query machinery.
 */
import type { InboxConversationDto, InboxListDto } from '@crm/types';

/**
 * Flatten the "All" tab's paged infinite-query result into one ordered, de-duped array.
 * De-dupe is by conversation id; the first occurrence (earliest/newest page) wins so the
 * server's last_message_at-DESC ordering is preserved.
 */
export function flattenInboxPages(pages: readonly InboxListDto[]): InboxConversationDto[] {
  const seen = new Set<string>();
  const merged: InboxConversationDto[] = [];
  for (const page of pages) {
    for (const conv of page.conversations) {
      if (seen.has(conv.id)) continue;
      seen.add(conv.id);
      merged.push(conv);
    }
  }
  return merged;
}

/**
 * Next keyset cursor for useInfiniteQuery's getNextPageParam. Returns undefined when there
 * are no further pages (the last page had hasMore=false, or no usable cursor could be
 * derived) — that signals React Query that pagination is exhausted.
 */
export function nextInboxCursor(lastPage: InboxListDto): string | undefined {
  if (!lastPage.hasMore) return undefined;
  // Prefer a server-emitted cursor if a future backend starts sending one; otherwise the
  // keyset key is the last row's lastMessageAt.
  if (lastPage.nextCursor) return lastPage.nextCursor;
  const last = lastPage.conversations[lastPage.conversations.length - 1];
  return last?.lastMessageAt ?? undefined;
}
